import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { THEMEPARKS_WIKI_SOURCE } from "../config/themeparksWiki";
import type { Entity, EntityType } from "../types";

interface EntityLike {
  type?: EntityType;
  parentId?: string | null;
  externalSource?: string | null;
  externalId?: string | null;
}

const MAX_HOPS = 8;

/**
 * Walk up an entity's `parentId` chain to find the owning PARK that's linked to
 * ThemeParks.wiki, and return that park's wiki entity id. If the entity is
 * itself a wiki-linked PARK, returns its own id. Returns null when there's no
 * wiki-linked park in the ancestry.
 */
export function useOwningParkExternalId(entity: EntityLike | null | undefined) {
  const parentId = entity?.parentId ?? null;
  const type = entity?.type ?? null;
  const externalSource = entity?.externalSource ?? null;
  const externalId = entity?.externalId ?? null;

  const selfParkId =
    type === "PARK" && externalSource === THEMEPARKS_WIKI_SOURCE && externalId ? externalId : null;

  const [parkExternalId, setParkExternalId] = useState<string | null>(selfParkId);
  const [loading, setLoading] = useState<boolean>(selfParkId == null && !!parentId);

  useEffect(() => {
    if (selfParkId) {
      setParkExternalId(selfParkId);
      setLoading(false);
      return;
    }
    if (!parentId) {
      setParkExternalId(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setParkExternalId(null);
    (async () => {
      let currentId: string | null = parentId;
      let hops = 0;
      while (currentId && hops < MAX_HOPS) {
        hops++;
        try {
          const snap = await getDoc(doc(db, "entities", currentId));
          if (!snap.exists()) break;
          const data = snap.data() as Entity;
          if (
            data.type === "PARK" &&
            data.externalSource === THEMEPARKS_WIKI_SOURCE &&
            typeof data.externalId === "string" &&
            data.externalId.length > 0
          ) {
            if (!cancelled) {
              setParkExternalId(data.externalId);
              setLoading(false);
            }
            return;
          }
          currentId = data.parentId ?? null;
        } catch {
          break;
        }
      }
      if (!cancelled) {
        setParkExternalId(null);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [parentId, selfParkId]);

  return { parkExternalId, loading };
}
